export const CREATE_SKILL_SLASH = 'create-skill';

export type SlashDraft = {
  slash: string;
  remainder: string;
};

/** First token after a leading `/`, or null when the draft is not a slash command. */
export function parseSlashDraft(text: string): SlashDraft | null {
  const t = String(text ?? '').trim();
  if (!t.startsWith('/')) {
    return null;
  }
  const rest = t.slice(1);
  let slash = '';
  let i = 0;
  for (; i < rest.length; i++) {
    const ch = rest[i];
    if (ch === ' ' || ch === '\n' || ch === '\t') {
      break;
    }
    slash += ch.toLowerCase();
  }
  return {
    slash,
    remainder: rest.slice(i).trim(),
  };
}

export function slashFromSkillName(raw: string): string {
  let s = String(raw ?? '').trim();
  if (s.startsWith('/')) {
    s = s.slice(1).trim();
  }
  s = s.toLowerCase();
  const parts: string[] = [];
  let current = '';
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    const code = s.charCodeAt(i);
    const isLower = code >= 97 && code <= 122;
    const isDigit = code >= 48 && code <= 57;
    if (isLower || isDigit) {
      current += ch;
      continue;
    }
    if (ch === '-' || ch === '_' || ch === ' ' || ch === '\t' || ch === '\n') {
      if (current !== '') {
        parts.push(current);
        current = '';
      }
    }
  }
  if (current !== '') {
    parts.push(current);
  }
  return parts.join('-');
}

export function skillSlashStartsWith(slash: string, prefix: string): boolean {
  if (!prefix) {
    return true;
  }
  if (slash.length < prefix.length) {
    return false;
  }
  return slash.slice(0, prefix.length) === prefix;
}

/** `/name` label for a chat bubble. Empty when there is no slash. */
export function formatSkillSlashLabel(slash: string): string {
  let name = String(slash ?? '').trim();
  if (name.startsWith('/')) {
    name = name.slice(1).trim();
  }
  return name ? `/${name}` : '';
}

/** Reads `metadata.skillSlash` saved with the user message. */
export function skillSlashLabelFromMetadata(metadata: unknown): string {
  if (metadata == null || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return '';
  }
  const raw = (metadata as Record<string, unknown>)['skillSlash'];
  if (typeof raw !== 'string') {
    return '';
  }
  return formatSkillSlashLabel(raw);
}

/** Raw slash token from conversation or message metadata (no leading `/`). Empty when absent. */
export function skillSlashTokenFromMetadata(metadata: unknown): string {
  if (metadata == null || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return '';
  }
  const raw = (metadata as Record<string, unknown>)['skillSlash'];
  if (typeof raw !== 'string') {
    return '';
  }
  let name = raw.trim();
  if (name.startsWith('/')) {
    name = name.slice(1).trim();
  }
  return name;
}

/** True when conversation list row was started (or used) with a skill. */
export function conversationHasSkill(metadata: unknown): boolean {
  return skillSlashTokenFromMetadata(metadata).length > 0;
}
